// Localização: frontend/src/hooks/useAgendaTarefas.js (versão 0.1.7)
// Adicionando suporte a busca global e edição de tarefa

import { useState, useCallback } from 'react';
import api from '../api/api';

/**
 * Hook personalizado para buscar e gerenciar o estado das tarefas agendadas.
 * Se 'pacienteId' for fornecido, busca tarefas para o paciente.
 * Se 'pacienteId' for nulo, busca todas as tarefas da organização (uso global na Agenda).
 * @param {number | null} pacienteId ID do paciente ou null para busca global.
 * @returns {object} O estado, funções de busca e manipulação de tarefas.
 */
export function useAgendaTarefas(pacienteId) {
    const [tarefas, setTarefas] = useState([]);
    const [isLoadingTarefas, setIsLoadingTarefas] = useState(false);
    const [errorTarefas, setErrorTarefas] = useState(null);

    // 🛑 MODIFICADO: Função fetchTarefasPendentes agora é flexível
    // No Diário (com pacienteId), ela carrega as pendentes.
    // Na Agenda (sem pacienteId), ela carrega todas.
    const fetchTarefasPendentes = useCallback(async () => {
        setIsLoadingTarefas(true);
        setErrorTarefas(null);
        
        try {
            let url;
            
            if (pacienteId) {
                // Diário de Cuidados: Buscar tarefas pendentes de um paciente específico.
                // Tenta primeiro a rota do diário (que usa permissão de diario), se falhar, tenta a rota de agenda_tarefas
                try {
                    const responseDiario = await api.get(`/diario/paciente/${pacienteId}/tarefas`);
                    setTarefas(responseDiario.data.data || responseDiario.data || []);
                    return; // Sucesso, retorna sem continuar
                } catch (errDiario) {
                    // Se a rota do diário não existir ou falhar, tenta a rota original
                    if (errDiario.response?.status !== 404) {
                        console.warn('Rota alternativa do diário não disponível, tentando rota padrão:', errDiario);
                    }
                }
                url = `/agenda-tarefas/paciente/${pacienteId}`;
            } else {
                // Agenda Global: Buscar todas as tarefas (para posterior filtragem no frontend).
                url = `/agenda-tarefas`; 
            }
            
            const response = await api.get(url);
            
            // O backend deve retornar um array em response.data.data ou apenas response.data
            // Assumo que a lista está em response.data.data com base no seu `fetchTarefaById`
            setTarefas(response.data.data || response.data || []); 
            
        } catch (err) {
            // 🛑 CORREÇÃO: Tratar 403 (sem permissão) como não crítico
            // Se o usuário não tem permissão para agenda_tarefas, apenas não mostra as tarefas
            // mas não impede o uso do diário
            if (err.response?.status === 403) {
                console.log('Usuário não tem permissão para agenda_tarefas. Tarefas não serão exibidas.');
                setErrorTarefas(null); // Não define erro para 403
                setTarefas([]); // Lista vazia, mas sem erro
            } else {
                console.error('Erro ao buscar tarefas:', err);
                const message = err.response?.data?.message || 'Falha ao carregar tarefas agendadas.';
                setErrorTarefas(message);
                setTarefas([]);
            }
        } finally {
            setIsLoadingTarefas(false);
        }
    }, [pacienteId]);


    /**
     * @desc Busca uma tarefa agendada completa pelo ID. Essencial para edição.
     * @param {number} tarefaId ID da tarefa a ser buscada.
     * @returns {object} Dados completos da tarefa.
     */
    const fetchTarefaById = useCallback(async (tarefaId) => {
        if (!tarefaId) {
            throw new Error('ID da tarefa é obrigatório para a busca.');
        }

        try {
            // Rota mapeada: GET /api/agenda-tarefas/:id
            const response = await api.get(`/agenda-tarefas/${tarefaId}`);
            
            // O backend retorna { message: "Tarefa carregada com sucesso.", data: {...} }
            return response.data.data;
        } catch (err) {
            console.error(`Erro ao buscar tarefa ${tarefaId}:`, err);
            const errorMessage = err.response?.data?.message || 'Falha ao carregar detalhes da tarefa.';
            throw new Error(errorMessage); 
        }
    }, []); 
    
    // Função para remover uma tarefa da lista (útil após a conclusão no useDiarioCuidados) (Inalterada)
    const removeTarefaDaLista = useCallback((agenda_tarefa_id) => {
        setTarefas(prev => prev.filter(t => t.id !== agenda_tarefa_id));
    }, []);

    const createTarefaSimples = useCallback(async (payload) => {
        const response = await api.post(`/agenda-tarefas`, payload);
        
        if (payload.paciente_id === pacienteId) {
            fetchTarefasPendentes(); 
        }

        return response.data;
    }, [fetchTarefasPendentes, pacienteId]);


    // 🛑 NOVO: Função para atualizar uma tarefa individual (Edição)
    const updateTarefa = useCallback(async (tarefaId, payload) => {
        if (!tarefaId) {
            throw new Error("ID da tarefa é obrigatório para atualização.");
        }
        
        // Rota mapeada: PUT /api/agenda-tarefas/:id
        const response = await api.put(`/agenda-tarefas/${tarefaId}`, payload);
        
        // Atualização da lista (recarga completa ou apenas atualiza o item)
        // Optamos por recarregar a lista para garantir a consistência dos filtros
        fetchTarefasPendentes();

        return response.data;
    }, [fetchTarefasPendentes]);

    // 🛑 NOVO: Função para criar uma tarefa recorrente (Inalterada)
    const createTarefaRecorrente = useCallback(async (payload) => {
        const response = await api.post(`/agenda-tarefas/recorrente`, payload);
        
        if (payload.paciente_id === pacienteId) {
            fetchTarefasPendentes(); 
        }

        return response.data;
    }, [fetchTarefasPendentes, pacienteId]);

    /**
     * @desc Atualiza uma regra de agendamento recorrente e suas futuras ocorrências.
     * @param {number} tarefaId ID da tarefa/regra base a ser atualizada.
     * @param {object} payload Dados completos da recorrência.
     * @returns {object} Mensagem de sucesso.
     */
    // 🎯 NOVO: Rota e função dedicadas para a Regra de Recorrência
    const updateTarefaRecorrente = useCallback(async (tarefaId, payload) => {
        if (!tarefaId) {
            throw new Error("ID da regra de recorrência é obrigatório para atualização.");
        }
        
        // 🛑 Rota Dedicada: PUT /api/agenda-tarefas/recorrente/:id
        const response = await api.put(`/agenda-tarefas/recorrente/${tarefaId}`, payload);
        
        // Recarrega a lista para mostrar a alteração
        fetchTarefasPendentes(); 

        return response.data;
    }, [fetchTarefasPendentes]);



    // 🛑 CORREÇÃO: Função para excluir uma tarefa individual
    // Agora recarrega a lista do servidor após excluir para garantir sincronização
    const deleteTarefa = useCallback(async (tarefaId) => {
        try {
            await api.delete(`/agenda-tarefas/${tarefaId}`);
            
            // Remove da lista local imediatamente para feedback visual
            removeTarefaDaLista(tarefaId);
            
            // Recarrega a lista do servidor para garantir sincronização completa
            await fetchTarefasPendentes();

            return { message: 'Tarefa excluída com sucesso.' };
        } catch (error) {
            console.error('Erro ao excluir tarefa:', error);
            throw error;
        }
    }, [removeTarefaDaLista, fetchTarefasPendentes]);

    // 🛑 NOVO: Função para cancelar uma tarefa (marca como Cancelada)
    const cancelarTarefa = useCallback(async (tarefaId, motivoCancelamento) => {
        if (!motivoCancelamento || motivoCancelamento.trim() === '') {
            throw new Error('O motivo do cancelamento é obrigatório.');
        }

        try {
            // A rota de cancelamento é PUT /agenda-tarefas/cancelar/:id com motivo_cancelamento no body
            await api.put(`/agenda-tarefas/cancelar/${tarefaId}`, {
                motivo_cancelamento: motivoCancelamento.trim()
            });
            
            // Atualiza a lista removendo a tarefa cancelada ou atualizando seu status
            removeTarefaDaLista(tarefaId);

            return { message: 'Tarefa cancelada com sucesso.' };
        } catch (err) {
            console.error("Falha ao cancelar tarefa:", err);
            const errorMessage = err.response?.data?.message || 'Erro ao cancelar a tarefa.';
            throw new Error(errorMessage);
        }
    }, [removeTarefaDaLista]);

    const concluirTarefa = useCallback(async (agenda_tarefa_id, observacoes = null, sinaisVitais = null) => {
        try {
            const payload = {
                tarefaId: agenda_tarefa_id, 
                observacoes: observacoes 
            };

            // Se houver dados de sinais vitais, adiciona ao payload
            if (sinaisVitais && Object.keys(sinaisVitais).length > 0) {
                payload.sinaisVitais = sinaisVitais;
            }

            // Tenta primeiro a rota do diário (que usa permissão de diario), se falhar, tenta a rota de agenda_tarefas
            let response;
            try {
                response = await api.post(`/diario/concluir-tarefa`, payload);
            } catch (errDiario) {
                // Se a rota do diário não existir ou falhar, tenta a rota original
                if (errDiario.response?.status !== 404 && errDiario.response?.status !== 403) {
                    console.warn('Rota alternativa do diário não disponível, tentando rota padrão:', errDiario);
                }
                response = await api.post(`/agenda-tarefas/concluir`, payload);
            }
            
            return response.data;
            
        } catch (err) {
            console.error("Falha ao concluir tarefa:", err);
            const errorMessage = err.response?.data?.message || 'Erro de rede ou falha no servidor ao concluir a tarefa.';
            throw new Error(errorMessage); 
        }
    }, []);

    /**
     * @desc Busca tarefas concluídas de um paciente específico (para o Prontuário).
     * @param {number} pacienteId - ID do paciente.
     * @param {string | null} dateStart - Data de início do período (formato: 'YYYY-MM-DD'). Se null, busca últimas 30 dias.
     * @param {string | null} dateEnd - Data de fim do período (formato: 'YYYY-MM-DD'). Se null, usa data atual.
     * @returns {Array} Lista de tarefas concluídas.
     */
    const fetchTarefasConcluidas = useCallback(async (pacienteId, dateStart = null, dateEnd = null) => {
        if (!pacienteId) {
            throw new Error('ID do paciente é obrigatório para buscar tarefas concluídas.');
        }

        setIsLoadingTarefas(true);
        setErrorTarefas(null);

        try {
            let url = `/agenda-tarefas/paciente/${pacienteId}/concluidas`;
            
            // Adiciona query params se fornecidos
            const params = new URLSearchParams();
            if (dateStart) params.append('dateStart', dateStart);
            if (dateEnd) params.append('dateEnd', dateEnd);
            
            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await api.get(url);
            
            // O backend retorna { message: "...", data: [...] }
            const tarefas = response.data.data || [];
            
            setTarefas(tarefas);
            return tarefas;
            
        } catch (err) {
            console.error('Erro ao buscar tarefas concluídas:', err);
            const errorMessage = err.response?.data?.message || 'Falha ao carregar tarefas concluídas.';
            setErrorTarefas(errorMessage);
            setTarefas([]);
            throw new Error(errorMessage);
        } finally {
            setIsLoadingTarefas(false);
        }
    }, []);


    return {
        tarefas,
        isLoadingTarefas,
        errorTarefas,
        fetchTarefasPendentes,
        fetchTarefasConcluidas,
        removeTarefaDaLista,
        createTarefaSimples,
        updateTarefa, // 🎯 Exportando a nova função de atualização
        createTarefaRecorrente,
        updateTarefaRecorrente,
        deleteTarefa, 
        cancelarTarefa, // 🎯 Nova função para cancelar tarefas
        concluirTarefa,
        fetchTarefaById, // Já estava aqui
    };
}